import { type ApolloError, type FetchResult, useMutation, useQuery } from '@apollo/client';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Button, CircularProgress, Container, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useNavigate } from '@tanstack/react-router';
import { type FormikErrors, type FormikHelpers, useFormik } from 'formik';
import React, { type MouseEvent, useEffect, useState } from 'react';
import useStyles from 'src/AkomiLoginPage/ResetPasswordPage/ResetPasswordPageStyles';
import resetPasswordRequestInfo from 'src/api/resetPassword/info';
import updatePassword from 'src/api/resetPassword/update';
import Logo from 'src/components/logo';
import NotFound from 'src/components/NotFound/NotFound';
import * as Yup from 'yup';

interface UpdatePasswordFormValues {
  password: string;
  repeatPassword: string;
}

interface ResetPasswordRequestInfoResponse {
  resetPasswordRequestInfo: {
    createdDatetime: string;
    expired: boolean;
    email: string;
  } | null;
}

interface UpdatePasswordResponse {
  updatePassword: {
    success: boolean;
  };
}

interface UpdatePasswordVariables {
  newPassword: string;
  requestId: string;
}

const UpdatePasswordPage: React.FC = () => {
  const requestId = window.location.pathname.split('reset-password/')[1];
  const classes = useStyles();
  const navigate = useNavigate();
  const [success, setSuccess] = useState<boolean>(false);
  const [passwordVisibility1, setPasswordVisibility1] = useState<boolean>(false);
  const [passwordVisibility2, setPasswordVisibility2] = useState<boolean>(false);
  const [sendUpdatePasswordRequest] = useMutation<UpdatePasswordResponse, UpdatePasswordVariables>(updatePassword);
  const { loading, error, data } = useQuery<ResetPasswordRequestInfoResponse>(resetPasswordRequestInfo, {
    variables: { requestId: requestId },
  });
  const [linkError, setLinkError] = useState<boolean>(false);
  const [resetError, setResetError] = useState<string>('');

  const formik = useFormik<UpdatePasswordFormValues>({
    initialValues: {
      password: '',
      repeatPassword: '',
    },
    validationSchema: Yup.object({
      password: Yup.string().min(4, 'Password must be at least 4 characters').required('This field is required'),
      repeatPassword: Yup.string().required('This field is required'),
    }),
    validate: (values: UpdatePasswordFormValues): FormikErrors<UpdatePasswordFormValues> => {
      if (values.password && values.repeatPassword !== values.password) {
        return { repeatPassword: 'Passwords do not match' };
      }
      return {};
    },
    onSubmit: async (
      values: UpdatePasswordFormValues,
      _helpers: FormikHelpers<UpdatePasswordFormValues>,
    ): Promise<void> => {
      const { password } = values;
      setResetError('');
      sendUpdatePasswordRequest({
        variables: { newPassword: password, requestId: requestId },
      })
        .then((resp: FetchResult<UpdatePasswordResponse>) => {
          if (resp.data?.updatePassword.success) {
            setSuccess(true);
          }
        })
        .catch((e: ApolloError) => {
          setResetError(e.message);
        });
    },
  });

  useEffect(() => {
    if (!loading && data) {
      if (data.resetPasswordRequestInfo?.expired) {
        setLinkError(true);
      }
    }
  }, [loading, data]);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
  };

  const togglePasswordVisibility1 = (): void => {
    setPasswordVisibility1(!passwordVisibility1);
  };

  const togglePasswordVisibility2 = (): void => {
    setPasswordVisibility2(!passwordVisibility2);
  };

  const goHome = (): void => {
    navigate({ to: '/' });
  };

  if (linkError || error) {
    return <NotFound />;
  }

  if (loading || !data) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height={'75vh'} width={'100%'}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <div className={classes.akomiPage}>
      <div className={classes.akomiLogo}>
        <Logo />
      </div>
      <div className={classes.resetPasswordForm}>
        <Container className={classes.container} component="main">
          {success ? (
            <div className={classes.successContainer}>
              <Typography component="h1" variant="h1" className={classes.title}>
                Password Updated Successfully
              </Typography>
              <Button variant="contained" color="primary" className={classes.submitButton} onClick={goHome}>
                Login
              </Button>
            </div>
          ) : (
            <form className={classes.form} onSubmit={formik.handleSubmit}>
              <Typography component="h1" variant="h1" className={classes.title}>
                Enter a New Password
              </Typography>
              <div className={classes.formElements}>
                <TextField
                  label="New password"
                  type={passwordVisibility1 ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  error={formik.touched.password && !!formik.errors.password}
                  helperText={formik.touched.password && formik.errors.password}
                  {...formik.getFieldProps('password')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={togglePasswordVisibility1}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {passwordVisibility1 ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <div className={classes.formElements}>
                <TextField
                  label="Confirm password"
                  type={passwordVisibility2 ? 'text' : 'password'}
                  variant="outlined"
                  fullWidth
                  error={formik.touched.repeatPassword && !!formik.errors.repeatPassword}
                  helperText={formik.touched.repeatPassword && formik.errors.repeatPassword}
                  {...formik.getFieldProps('repeatPassword')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={togglePasswordVisibility2}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {passwordVisibility2 ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <div className={classes.formElements}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!!formik.errors.password || !!formik.errors.repeatPassword}
                  className={classes.submitButton}
                >
                  Submit
                </Button>
              </div>
              {resetError && <Typography color="error">{resetError}</Typography>}
            </form>
          )}
        </Container>
      </div>
    </div>
  );
};

export default UpdatePasswordPage;
